/* eslint-disable max-lines */
import FormData from 'form-data';

import {PaymentInfoType} from 'desktop/types/PaymentInfoType';

import {PAYMENTS} from '../../constants/paymentStep';

/**
 * Preparation Data
 * @param {PaymentInfoType} paymentInfo - Payer information from payment step reducer
 * @param {FormData} data - Form data
 * @param {object} dataFormat - Object format data
 */
export const preparationData = (
    paymentInfo: PaymentInfoType,
    data: FormData,
    dataFormat: Record<string, keyof PaymentInfoType>,
) => {
    Object.keys(dataFormat).forEach((item: string) => {
        data.append(
            item,
            typeof paymentInfo[dataFormat[item]] === 'boolean'
                ? Number(paymentInfo[dataFormat[item]])
                : paymentInfo[dataFormat[item]] || '',
        );
    });
};

/**
 * Build form data
 * @param {PaymentInfoType} paymentInfo - Payer information from payment step reducer
 * @param {string} formKey - Form key
 * @return {FormData} - Form data for savePayment and placeOrder endpoints
 */
export const formDataDefault = (paymentInfo: PaymentInfoType, formKey: string): FormData => {
    const data = new FormData();

    data.append('form_key', formKey);
    data.append('subscribe-newsletter', Number(paymentInfo.subscribe));
    data.append('payment[method]', paymentInfo.method || '');
    data.append('payment[walkway]', 'checkout');
    data.append('payment[terms]', 1);
    data.append('affid', paymentInfo.affid || '');

    return data;
};

/**
 * Build Paypal form data
 * @param {PaymentInfoType} paymentInfo - Payer information from payment step reducer
 * @return {FormData} - Form data for savePayment and placeOrder endpoints
 */
export const formDataPaypal = (paymentInfo: PaymentInfoType): FormData => {
    const data = new FormData();

    data.append('payer_id', paymentInfo.payer_id);
    data.append('payment_id', paymentInfo.payment_id);
    data.append('payment[method]', 'paypal_express');
    data.append('payment[walkway]', 'checkout');
    data.append('subscribe-newsletter', Number(paymentInfo.subscribe));
    data.append('affid', paymentInfo.affid || '');

    return data;
};

/**
 * Build GooglePay form data
 * @param {PaymentInfoType} paymentInfo - Payer information from payment step reducer
 * @param {string} formKey - Form key
 * @return {FormData} - Form data for savePayment and placeOrder endpoints
 */
export const formDataGooglePay = (paymentInfo: PaymentInfoType, formKey: string): FormData => {
    const formDataMapGooglePay: Record<string, keyof PaymentInfoType> = {
        'payment[method]': 'method',
        'payment[payment_method_id]': 'token',
        'payment[token]': 'token',
    };
    const data = new FormData();

    data.append('form_key', formKey);

    preparationData(paymentInfo, data, formDataMapGooglePay);

    return data;
};

/**
 * Build PayPalV3 form data
 * @param {PaymentInfoType} paymentInfo - Payer information from payment step reducer
 * @param {string} formKey - Form key
 * @return {FormData} - Form data for savePayment and placeOrder endpoints
 */
export const formDataPayPalV3 = (paymentInfo: PaymentInfoType, formKey: string): FormData => {
    const formDataMapGooglePay: Record<string, keyof PaymentInfoType> = {
        'payment[method]': 'method',
        'payment[payment_id]': 'id',
        'payment[token]': 'token',
    };
    const data = new FormData();

    data.append('form_key', formKey);

    preparationData(paymentInfo, data, formDataMapGooglePay);

    return data;
};

/**
 * Build amazon v2 form data
 * @param {PaymentInfoType} paymentInfo - Payer information from payment step reducer
 * @param {string} formKey - Form key
 * @return {FormData} - Form data for savePayment and placeOrder endpoints
 */
export const formDataAmazonV2ForSavePayment = (
    paymentInfo: PaymentInfoType,
    formKey: string,
): FormData => {
    const formDataMapAmazon: Record<string, keyof PaymentInfoType> = {
        'payment[method]': 'method',
        'payment[session_id]': 'accessToken',
    };
    const data = new FormData();

    data.append('form_key', formKey);

    preparationData(paymentInfo, data, formDataMapAmazon);

    return data;
};

/**
 * Build amazon v2 form data
 * @param {PaymentInfoType} paymentInfo - Payer information from payment step reducer
 * @return {FormData} - Form data for savePayment and placeOrder endpoints
 */
export const formDataAmazonV2ForOrder = (paymentInfo: PaymentInfoType): FormData => {
    const formDataMapAmazon: Record<string, keyof PaymentInfoType> = {
        'payment[method]': 'method',
        'payment[session_id]': 'accessToken',
    };
    const data = new FormData();

    preparationData(paymentInfo, data, formDataMapAmazon);

    return data;
};

/**
 * Create form data for paymentsOS
 *
 * @param {{token: string, save: boolean, affid: string}} cardData - credit card data
 * @returns {FormData} - Form data for savePayment endpoint
 */
export const formDataPaymentsOS = ({
    affid,
    save,
    token,
}: {
    token: string;
    save: boolean;
    affid: string;
}): FormData => {
    const data = new FormData();
    const saveCard = save || false;
    data.append('payment[method]', PAYMENTS.METHOD_CREDIT_CARD);
    data.append('payment[token]', token);
    data.append('payment[save_card]', saveCard ? '1' : '0');
    data.append('affid', affid || '');

    return data;
};

/**
 * Create form data for paymentsOS
 *
 * @param {{token: string, cvv: string, affid: string}} cardData - credit card data
 * @returns {FormData} - Form data for placeOrder endpoint
 */
export const orderFormDataPaymentsOS = ({
    affid,
    cvv,
    subscribe,
    token,
}: {
    token: string;
    cvv: string;
    affid: string;
    subscribe: boolean;
}): FormData => {
    const data = new FormData();
    data.append('payment[method]', PAYMENTS.METHOD_CREDIT_CARD);
    data.append('payment[token]', token);
    data.append('affid', affid || '');
    if (cvv) data.append('payment[encrypted_cvv]', cvv);
    data.append('subscribe-newsletter', Number(subscribe));
    return data;
};

/**
 * Create form data based on recurly response
 *
 * @param {{token: string, affid: string, method: string}} cardData - credit card data
 * @returns {FormData} - Form data for placeOrder endpoint
 */
export const formDataPaymentsRecurly = ({
    affid,
    method,
    subscribe,
    token,
}: PaymentInfoType): FormData => {
    const data = new FormData();
    data.append('payment[method]', method);
    data.append('payment[token]', token);
    data.append('affid', affid || '');
    if (subscribe) data.append('subscribe-newsletter', Number(subscribe));
    return data;
};

/**
 * Create form data based on klarna response
 *
 * @param {{token: string, affid: string}} cardData - credit card data
 * @returns {FormData} - Form data for placeOrder endpoint
 */
export const formDataPaymentsKlarna = ({affid, paymentId, token}: PaymentInfoType): FormData => {
    const data = new FormData();
    data.append('payment[method]', PAYMENTS.METHOD_KLARNA);
    data.append('payment[token]', token);
    data.append('payment[session_id]', paymentId);
    data.append('affid', affid || '');

    return data;
};

/**
 * Create form data based on klarna response
 *
 * @param {{token: string, affid: string}} cardData - credit card data
 * @returns {FormData} - Form data for placeOrder endpoint
 */
export const formDataPaymentsStripe = ({affid, paymentId}: PaymentInfoType): FormData => {
    const data = new FormData();
    data.append('payment[method]', PAYMENTS.METHOD_STRIPE);
    data.append('payment[payment_method_id]', paymentId);
    data.append('affid', affid || '');

    return data;
};

/**
 * Build ApplePay form data
 * @param {PaymentInfoType} paymentInfo - Payer information from payment step reducer
 * @param {string} formKey - Form key
 * @return {FormData} - Form data for savePayment and placeOrder endpoints
 */
export const formDataApplePay = (paymentInfo: PaymentInfoType, formKey: string): FormData => {
    const formDataMapApplePay: Record<string, keyof PaymentInfoType> = {
        'payment[method]': 'method',
        'payment[payment_method_id]': 'token',
        'payment[token]': 'token',
    };
    const data = new FormData();

    data.append('form_key', formKey);

    preparationData(paymentInfo, data, formDataMapApplePay);

    return data;
};

/**
 * Build Primer credit card form data
 * @param {PaymentInfoType} paymentInfo - Payer information from payment step reducer
 * @param {string} formKey - Form key
 * @return {FormData} - Form data for savePayment and placeOrder endpoints
 */
export const orderFormDataPrimerCC = (paymentInfo: PaymentInfoType, formKey: string): FormData => {
    const formDataMap: Record<string, keyof PaymentInfoType> = {
        'payment[external_payment_id]': 'token',
        'payment[method]': 'method',
    };
    const data = new FormData();
    if (paymentInfo.subscribe) data.append('subscribe-newsletter', Number(paymentInfo.subscribe));
    data.append('affid', paymentInfo.affid || '');
    data.append('form_key', formKey);
    data.append('payment[terms]', 1);

    preparationData(paymentInfo, data, formDataMap);

    return data;
};

/**
 * Build Venmo form data
 * @param {PaymentInfoType} paymentInfo - Payer information from payment step reducer
 * @param {string} formKey - Form key
 * @return {FormData} - Form data for savePayment and placeOrder endpoints
 */
export const orderFormDataVenmo = (paymentInfo: PaymentInfoType): FormData => {
    const formDataMapVenmo: Record<string, keyof PaymentInfoType> = {
        'payment[email]': 'email',
        'payment[firstName]': 'firstName',
        'payment[lastName]': 'lastName',
        'payment[method]': 'method',
        'payment[nonce]': 'nonce',
        'payment[phoneNumber]': 'phoneNumber',
    };
    const data = new FormData();

    data.append('affid', paymentInfo.affid || '');

    preparationData(paymentInfo, data, formDataMapVenmo);

    return data;
};
